import Sentry from "@similarweb/sentry-logger";

import { DeleteKeywordListConfirmationModal } from "app/components/modals/components/DeleteKeywordListConfirmationModal";
import { getQueryKey } from "app/modules/api-client";
import { createKeywordGroupEntity } from "app/modules/demand-analysis/keyword-lists";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import type { IDeleteConfirmationModalProps } from "app/pages/keyword-analysis/useKeywordGroupModalWorkflow";
import type { IKeywordsGroupEditorController } from "app/pages/keyword-analysis/useKeywordsGroupEditorController";
import { resolveAllowedCountry } from "app/pages/market-research/shared/countryResolution";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import queryClient from "single-spa/react-app/query-client";

interface IAutocompleteDemandAnalysisEditorModalProps {
    controller: IKeywordsGroupEditorController;
    closeEditorModal: () => void;
    handleBeforeDelete?: (group: IUserKeywordGroup) => void;
    deleteConfirmationModalProps?: IDeleteConfirmationModalProps;
    onGroupDelete?: (deletedGroup: IUserKeywordGroup) => void;
    onSave?: (group: IUserKeywordGroup) => void;
    showGoToMonitorKeywordListButton: boolean;
}

export const AutocompleteDemandAnalysisEditorModal = ({
    controller,
    closeEditorModal,
    handleBeforeDelete,
    deleteConfirmationModalProps,
    onGroupDelete,
    onSave,
    showGoToMonitorKeywordListButton,
}: IAutocompleteDemandAnalysisEditorModalProps) => {
    const handleGroupEdit = () => {
        queryClient.removeQueries({
            queryKey: getQueryKey({
                tag: "DemandSearchTrends",
                methodName: "marketResearchDemandAnalysisSearchTrendsList",
            }),
        });
    };

    const handleSuccessToastLinkClick = (keywordGroupId: string) => {
        try {
            const resolvedCountry = resolveAllowedCountry(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                WORLDWIDE_COUNTRY_ID,
            );

            const keywordGroupEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId },
                keywordGroupId,
            );

            void proNavigator.go(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                { id: [keywordGroupEntity.keywordListId], country: resolvedCountry },
                { inherit: false },
            );
        } catch (error) {
            Sentry.error(
                "[AutocompleteDemandAnalysisEditorModal] Failed to navigate to keyword group",
                error as Error,
                { tags: { team: "ants" } },
            );
        }
    };

    return (
        <>
            <KeywordsGroupEditorModal
                onClose={closeEditorModal}
                onDelete={handleBeforeDelete ? undefined : onGroupDelete}
                onBeforeDelete={handleBeforeDelete}
                onSave={onSave}
                onEdit={handleGroupEdit}
                onSuccessToastLinkClick={handleSuccessToastLinkClick}
                showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
                controller={controller}
            />
            {deleteConfirmationModalProps ? (
                <DeleteKeywordListConfirmationModal
                    {...deleteConfirmationModalProps}
                    dataAutomationPrefix="search-trends-delete-keyword-list-confirmation"
                />
            ) : null}
        </>
    );
};
